import API from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	CancellationClient,
	PaymentType,
	type ConfirmCancellationCommand,
} from '@acorn-portal/shared/src/services/cancellation-api';
import type { RootState } from '@acorn-portal/shared/src/store';
import { selectCancellation, selectRateData } from '@acorn-portal/shared/src/store/cancellation/cancellation.selectors';
import { updateStepCeiling, updateTakenUp } from '@acorn-portal/shared/src/store/cancellation/cancellation.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { PaymentType as PaymentOptionCode } from '@acorn-portal/shared/src/utils/types/payment';
import { useCallback, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useNavigate } from 'react-router';

const DEFAULT_PAYMENT_OPTION_REFUND = PaymentOptionCode.SINGLE_PAYMENT_DIRECT_CREDIT_RP;
const DEFAULT_PAYMENT_OPTION_CHARGE = PaymentOptionCode.SINGLE_PAYMENT_PAYMENT_CARD_AP;

/**
 * Hook for confirming the cancellation: calls the confirm API, then on success
 * navigates to the success page; on failure keeps the user on the page and
 * exposes confirmFailed so the UI can show a "Something went wrong" modal.
 */
export const useConfirmCancellation = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const store = useStore<RootState>();
	const [isConfirming, setIsConfirming] = useState(false);
	const [confirmFailed, setConfirmFailed] = useState(false);

	const handleConfirmCancellation = useCallback(async () => {
		setConfirmFailed(false);
		setIsConfirming(true);
		try {
			const state = store.getState();
			const policy = state.fullPolicy;
			const cancellation = selectCancellation(state);
			const rateData = selectRateData(state);
			const effectiveRateData = rateData ?? cancellation.cancellationRatingResponse?.rateData;
			const paymentCollectionOptionCode =
				effectiveRateData?.paymentOptionCode?.trim() ||
				(effectiveRateData?.paymentType === PaymentType.Refund
					? DEFAULT_PAYMENT_OPTION_REFUND
					: DEFAULT_PAYMENT_OPTION_CHARGE);
			const command: ConfirmCancellationCommand = {
				policyReference: policy.policyNumber,
				transactionId: cancellation.cancellationRatingResponse?.transactionId ?? '',
				optionId: effectiveRateData?.optionId ?? '',
				paymentCollectionOptionCode,
				cancellationDate: cancellation.cancellationRatingCommand?.cancellationDate ?? '',
				cancellationReason: cancellation.cancellationRatingCommand?.cancellationReason ?? '',
			};
			const client = new CancellationClient(import.meta.env.VITE_CANCELLATION_API, API);
			await client.confirm(policy.product, undefined, command);
			dispatch(updateStepCeiling('success'));
			dispatch(updateTakenUp(true));
			navigate(ROUTE_PATHS.cancellationSuccess);
		} catch {
			setConfirmFailed(true);
		} finally {
			setIsConfirming(false);
		}
	}, [store, dispatch, navigate]);

	const clearConfirmError = useCallback(() => {
		setConfirmFailed(false);
	}, []);

	return {
		handleConfirmCancellation,
		isConfirming,
		confirmFailed,
		clearConfirmError,
	};
};
